/**
 * Local Videos Configuration
 * 
 * Videos should be placed in public/videos/ folder structure:
 * 
 * public/videos/
 *   ├── video1.mp4
 *   ├── video2.mp4
 *   └── ...
 */

export interface VideoItem {
  id: number;
  name: string;
  videoPath: string; // Path relative to public/videos/
  description?: string;
  category?: string;
}

/**
 * Get video URL from path (handles .mp4 and other formats)
 * Videos are directly in public/videos/ folder
 */
export const getVideoUrl = (videoPath: string): string => {
  // Encode the filename to handle spaces and special characters
  // encodeURIComponent handles spaces, but we need to keep slashes if path has folders
  const parts = videoPath.split('/');
  const encodedParts = parts.map(part => encodeURIComponent(part));
  return `/videos/${encodedParts.join('/')}`;
};

// ============================================
// VIDEO CONFIGURATION
// Using only videos that actually exist in public/videos/
// ============================================

// Available video files (only use files that actually exist)
const AVAILABLE_VIDEOS: string[] = [
  '1 - Anime Edit.mp4',
  '100 - The greatest swordsman.mp4',
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
  '21 - Buggy X Shanks.mp4',
  '82 - One piece bounty.mp4',
  '91 - Roronoa Zoro Wallpaper.mp4',
];

// CAROUSEL VIDEOS - 6 videos (Homepage hero) - using fighting section videos
export const CAROUSEL_VIDEOS: string[] = [
  '91 - Roronoa Zoro Wallpaper.mp4',
  '100 - The greatest swordsman.mp4',
  '1 - Anime Edit.mp4',
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
];

// TOP TRENDING VIDEOS - 6 videos - using only available videos, repeating as needed
export const TOP_TRENDING_VIDEOS: string[] = [
  '100 - The greatest swordsman.mp4',
  '1 - Anime Edit.mp4',
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
  '91 - Roronoa Zoro Wallpaper.mp4',
];

// ADVENTURE VIDEOS - 6 videos
export const ADVENTURE_VIDEOS: string[] = [
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
  '1 - Anime Edit.mp4',
  '100 - The greatest swordsman.mp4',
  '21 - Buggy X Shanks.mp4',
];

// ACTION VIDEOS - 6 videos
export const ACTION_VIDEOS: string[] = [
  '100 - The greatest swordsman.mp4',
  '91 - Roronoa Zoro Wallpaper.mp4',
  '1 - Anime Edit.mp4',
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
];

// BRAIN TEASE VIDEOS - 6 videos
export const BRAINTEASE_VIDEOS: string[] = [
  '82 - One piece bounty.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
  '21 - Buggy X Shanks.mp4',
  '1 - Anime Edit.mp4',
  '100 - The greatest swordsman.mp4',
  '104 - The life luffy saved___.mp4',
];

// FIGHTING VIDEOS - 6 videos
export const FIGHTING_VIDEOS: string[] = [
  '91 - Roronoa Zoro Wallpaper.mp4',
  '100 - The greatest swordsman.mp4',
  '1 - Anime Edit.mp4',
  '104 - The life luffy saved___.mp4',
  '156 - Luffy X Boa.mp4',
  '160 - Mr 3 Saved Luffy.mp4',
];

// Helper to extract video title from filename
const getVideoTitle = (filename: string): string => {
  // Remove .mp4 or .mov extension and number prefix if present
  const withoutExt = filename.replace(/\.(mp4|mov)$/i, '');
  // Remove leading number and dash if present (e.g., "1 - " or "100 - ")
  const title = withoutExt.replace(/^\d+\s*-\s*/, '').trim();
  return title || filename;
};

// Global counter for unique IDs
let globalVideoIdCounter = 1;

// Helper function to create video items
const createVideoItems = (
  videoPaths: string[],
  names: string[],
  descriptions?: string[],
  category?: string
): VideoItem[] => {
  return videoPaths.map((path, index) => ({
    id: globalVideoIdCounter++,
    name: names[index] || getVideoTitle(path),
    videoPath: path,
    description: descriptions?.[index],
    category
  }));
};

// Export video arrays
export const carouselVideos: VideoItem[] = createVideoItems(
  CAROUSEL_VIDEOS,
  CAROUSEL_VIDEOS.map(getVideoTitle),
  [
    'Roronoa Zoro Wallpaper',
    'Epic swordsman moment',
    'Featured anime edit collection',
    'The life luffy saved',
    'Luffy X Boa',
    'Mr 3 Saved Luffy',
  ]
);

export const topTrendingVideos: VideoItem[] = createVideoItems(
  TOP_TRENDING_VIDEOS,
  TOP_TRENDING_VIDEOS.map(getVideoTitle),
  [],
  'Top Trending'
);

export const adventureVideos: VideoItem[] = createVideoItems(
  ADVENTURE_VIDEOS,
  ADVENTURE_VIDEOS.map(getVideoTitle),
  [],
  'Adventure'
);

export const actionVideos: VideoItem[] = createVideoItems(
  ACTION_VIDEOS,
  ACTION_VIDEOS.map(getVideoTitle),
  [],
  'Action'
);

export const brainteaseVideos: VideoItem[] = createVideoItems(
  BRAINTEASE_VIDEOS,
  BRAINTEASE_VIDEOS.map(getVideoTitle),
  [],
  'Brain Tease'
);

export const fightingVideos: VideoItem[] = createVideoItems(
  FIGHTING_VIDEOS,
  FIGHTING_VIDEOS.map(getVideoTitle),
  [],
  'Fighting'
);

// Combined for favorites/videos page
export const allVideos = [
  ...carouselVideos.map(v => ({ ...v, category: 'Carousel' })),
  ...topTrendingVideos,
  ...adventureVideos,
  ...actionVideos,
  ...brainteaseVideos,
  ...fightingVideos
];
